import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Store scroll positions in sessionStorage to persist across navigation
const SCROLL_POSITION_KEY = 'inventory_scroll_positions';

interface ScrollPosition {
  [key: string]: number;
}

// Safari iOS detection
const isSafariIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && 
         /Safari/.test(navigator.userAgent) && 
         !/Chrome/.test(navigator.userAgent);
};

// Track if we're navigating back (Safari iOS specific)
let isNavigatingBack = false;

// Listen for popstate events to detect back navigation
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    // console.log('🔄 Safari iOS: popstate detected - setting isNavigatingBack to true');
    isNavigatingBack = true;
    // Reset the flag after a short delay
    setTimeout(() => {
      // console.log('🔄 Safari iOS: resetting isNavigatingBack to false');
      isNavigatingBack = false;
    }, 1000);
  });
}

export const useScrollPosition = (containerRef: React.RefObject<HTMLElement>) => {
  const location = useLocation();
  const isRestoring = useRef(false);
  const lastSavedPosition = useRef<number>(0);
  const isSafari = isSafariIOS();
  
  // Add state to track if we're waiting for scroll restoration
  const [isWaitingForScrollRestoration, setIsWaitingForScrollRestoration] = useState(false);
  
  // Add refs to track scroll restoration cancellation
  const shouldCancelRestoration = useRef(false);
  const restorationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get stored scroll positions
  const getStoredPositions = (): ScrollPosition => {
    try {
      const stored = sessionStorage.getItem(SCROLL_POSITION_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  // Function to cancel scroll restoration
  const cancelScrollRestoration = useCallback(() => {
    // console.log('🔄 Cancelling scroll restoration due to user scroll');
    shouldCancelRestoration.current = true;
    setIsWaitingForScrollRestoration(false);
    isRestoring.current = false;
    
    // Clear any pending restoration timeouts
    if (restorationTimeoutRef.current) {
      clearTimeout(restorationTimeoutRef.current);
      restorationTimeoutRef.current = null;
    }
  }, []);

  // Check if we have a saved scroll position for the current location
  const checkForSavedScrollPosition = useCallback(() => {
    const positions = getStoredPositions();
    const key = location.pathname + location.search;
    const savedPosition = positions[key];
    
    if (savedPosition !== undefined) {
      // console.log('🔄 Found saved scroll position:', savedPosition, 'for key:', key);
      setIsWaitingForScrollRestoration(true);
      shouldCancelRestoration.current = false; // Reset cancellation flag
      return true;
    } else {
      // console.log('🔄 No saved scroll position found for key:', key);
      setIsWaitingForScrollRestoration(false);
      return false;
    }
  }, [location.pathname, location.search]);

  // Check for saved scroll position immediately when location changes
  useEffect(() => {
    const hasSavedPosition = checkForSavedScrollPosition();
    
    // If we're waiting for scroll restoration, set a timeout to prevent infinite loading
    if (hasSavedPosition) {
      restorationTimeoutRef.current = setTimeout(() => {
        // console.log('🔄 Timeout reached, clearing scroll restoration waiting state');
        setIsWaitingForScrollRestoration(false);
      }, 3000); // 3 second timeout
      
      return () => {
        if (restorationTimeoutRef.current) {
          clearTimeout(restorationTimeoutRef.current);
          restorationTimeoutRef.current = null;
        }
      };
    }
  }, [location, checkForSavedScrollPosition]);

  // Find the actual scrollable element (CustomScrollbars div or container itself)
  const findScrollableElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    
    // console.log('🔍 Looking for scrollable element in container:', containerRef.current);
    
    // First try to find by data-testid
    const scrollableElement = containerRef.current.querySelector('[data-testid="product-browser-scroll"]') as HTMLElement;
    if (scrollableElement) {
      // console.log('🔍 Found scrollable element by data-testid');
      return scrollableElement;
    }
    
    // Look for the CustomScrollbars div (div with overflow:auto)
    const overflowElement = containerRef.current.querySelector('div[style*="overflow: auto"], div[style*="overflow:auto"]') as HTMLElement;
    if (overflowElement) {
      // console.log('🔍 Found scrollable element by overflow style');
      return overflowElement;
    }
    
    // Look for any div with overflow auto/scroll in computed styles
    const allDivs = containerRef.current.querySelectorAll('div');
    for (let i = 0; i < allDivs.length; i++) {
      const div = allDivs[i];
      const computedStyle = window.getComputedStyle(div);
      if (computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll') {
        // console.log('🔍 Found scrollable element by computed style:', div);
        return div as HTMLElement;
      }
    }
    
    // Fallback to the container itself if it's scrollable
    const computedStyle = window.getComputedStyle(containerRef.current);
    if (computedStyle.overflow === 'auto' || computedStyle.overflow === 'scroll') {
      // console.log('🔍 Using container as scrollable element');
      return containerRef.current;
    }
    
    // console.log('🔍 No scrollable element found');
    return null;
  }, [containerRef]);

  // Save scroll position for current location
  const saveScrollPosition = useCallback(() => {
    const scrollableElement = findScrollableElement();
    if (!scrollableElement) {
      // console.log('💾 No scrollable element found for saving position');
      return;
    }
    
    const currentPosition = scrollableElement.scrollTop;
    
    // Don't save if position hasn't changed significantly (prevents unnecessary saves)
    if (Math.abs(currentPosition - lastSavedPosition.current) < 10) {
      // console.log('💾 Position change too small, not saving:', currentPosition, 'vs', lastSavedPosition.current);
      return;
    }
    
    lastSavedPosition.current = currentPosition;
    
    const positions = getStoredPositions();
    const key = location.pathname + location.search;
    positions[key] = currentPosition;
    
    try {
      sessionStorage.setItem(SCROLL_POSITION_KEY, JSON.stringify(positions));
      // console.log('💾 Saved scroll position:', currentPosition, 'for key:', key);
    } catch (error) {
      console.error('💾 Error saving scroll position:', error);
    }
  }, [findScrollableElement, location.pathname, location.search]);

  // Restore scroll position for current location
  const restoreScrollPosition = useCallback(() => {
    // Check if restoration should be cancelled
    if (shouldCancelRestoration.current) {
      // console.log('🔄 Scroll restoration cancelled');
      return;
    }
    
    const scrollableElement = findScrollableElement();
    if (!scrollableElement) {
      // console.log('🔄 No scrollable element found for restoration');
      setIsWaitingForScrollRestoration(false); // Clear waiting state if no element found
      return;
    }
    
    const positions = getStoredPositions();
    const key = location.pathname + location.search;
    const savedPosition = positions[key];
    
    if (savedPosition !== undefined) {
      // console.log('🔄 Restoring scroll position:', savedPosition, 'for key:', key);
      isRestoring.current = true;
      // Don't set waiting state here - it's already set by checkForSavedScrollPosition
      
      // Wait for the element to be ready (have content and proper dimensions)
      const waitForElementReady = () => {
        // Check if restoration should be cancelled
        if (shouldCancelRestoration.current) {
          // console.log('🔄 Scroll restoration cancelled during waitForElementReady');
          isRestoring.current = false;
          setIsWaitingForScrollRestoration(false);
          return;
        }
        
        const rect = scrollableElement.getBoundingClientRect();
        const hasContent = scrollableElement.scrollHeight > 0;
        
        if (rect.height > 0 && hasContent) {
          // console.log('🔄 Element is ready, proceeding with scroll restoration');
          performScrollRestoration();
        } else {
          // console.log('🔄 Element not ready yet, retrying...', { height: rect.height, scrollHeight: scrollableElement.scrollHeight });
          setTimeout(waitForElementReady, 100);
        }
      };
      
      const performScrollRestoration = () => {
        // Check if restoration should be cancelled
        if (shouldCancelRestoration.current) {
          // console.log('🔄 Scroll restoration cancelled during performScrollRestoration');
          isRestoring.current = false;
          setIsWaitingForScrollRestoration(false);
          return;
        }
        
        // For Safari iOS, we need to be more aggressive about scroll restoration
        if (isSafari) {
          // Try multiple approaches for Safari iOS with longer delays
          const attemptScroll = (attempt: number = 0) => {
            // Check if restoration should be cancelled
            if (shouldCancelRestoration.current) {
              // console.log('🔄 Scroll restoration cancelled during attemptScroll');
              isRestoring.current = false;
              setIsWaitingForScrollRestoration(false);
              return;
            }
            
            if (attempt >= 3) {
              // console.log('🔄 Max attempts reached for Safari scroll restoration');
              isRestoring.current = false;
              setIsWaitingForScrollRestoration(false);
              return;
            }
            
            // Try to scroll to the saved position
            scrollableElement.scrollTop = savedPosition;
            
            // Check if scroll was successful
            setTimeout(() => {
              if (Math.abs(scrollableElement.scrollTop - savedPosition) < 10) {
                // console.log('🔄 Safari scroll restoration successful on attempt:', attempt + 1);
                isRestoring.current = false;
                setIsWaitingForScrollRestoration(false);
              } else {
                // console.log('🔄 Safari scroll restoration failed on attempt:', attempt + 1);
                attemptScroll(attempt + 1);
              }
            }, 200);
          };
          
          attemptScroll();
        } else {
          // For non-Safari browsers, use standard approach
          scrollableElement.scrollTop = savedPosition;
          
          // Clear restoration state after a short delay
          setTimeout(() => {
            isRestoring.current = false;
            setIsWaitingForScrollRestoration(false); // Clear waiting state
          }, 100);
        }
      };
      
      // Start the restoration process
      waitForElementReady();
    } else {
      // console.log('🔄 No saved scroll position found for key:', key);
      // If no saved position, we're not waiting for restoration
      setIsWaitingForScrollRestoration(false);
    }
  }, [findScrollableElement, location.pathname, location.search, isSafari]);

  // Save scroll position when component unmounts or location changes
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    const handlePageHide = () => {
      // Safari iOS specific event
      saveScrollPosition();
    };

    // Save position before navigating away
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      saveScrollPosition();
    };
  }, [location, saveScrollPosition]);

  // Restore scroll position when component mounts
  useEffect(() => {
    // For Safari iOS back navigation, we need to be more aggressive
    const delay = isSafari && isNavigatingBack ? 800 : (isSafari ? 500 : 200); // Increased delays
    const maxAttempts = isSafari && isNavigatingBack ? 8 : (isSafari ? 5 : 2); // Increased attempts
    
    let attempt = 0;
    const attemptRestore = () => {
      attempt++;
      // console.log(`🔄 Scroll restoration attempt ${attempt}/${maxAttempts} for Safari: ${isSafari}, navigating back: ${isNavigatingBack}`);
      restoreScrollPosition();
      
      // For Safari, try multiple times if needed
      if (isSafari && attempt < maxAttempts) {
        setTimeout(attemptRestore, 300); // Increased delay between attempts
      }
    };
    
    const timer = setTimeout(attemptRestore, delay);

    return () => clearTimeout(timer);
  }, [location, isSafari, restoreScrollPosition]);

  // Save scroll position on scroll events (debounced) and cancel restoration if user scrolls
  useEffect(() => {
    const scrollableElement = findScrollableElement();
    if (!scrollableElement) return;

    let timeoutId: NodeJS.Timeout;
    
    const handleScroll = () => {
      // Cancel scroll restoration if user starts scrolling while we're waiting for restoration
      if (isWaitingForScrollRestoration || isRestoring.current) {
        cancelScrollRestoration();
      }
      
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        saveScrollPosition();
      }, isSafari ? 50 : 100); // Faster debounce for Safari
    };

    scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollableElement.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [location, isSafari, isWaitingForScrollRestoration, isRestoring, cancelScrollRestoration, saveScrollPosition, findScrollableElement]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    cancelScrollRestoration,
    isRestoring: isRestoring.current,
    isNavigatingBack,
    isWaitingForScrollRestoration
  };
}; 